<script>
import api from '@utils/api'
import mixins from '../apimixins'
import Parameters from './parameters'
import moment from 'moment'
import Chart from 'chart.js'
import getColor from '@utils/colors.js'

export default {
  name: 'OilVolumeChange',
  components: {
    Parameters,
  },
  mixins: [mixins],
  props: {
    extParams: {
      type: Object,
      default: null,
    },
    overrideParams: {
      type: Object,
      default: function() {
        return null
      },
    },
  },
  data() {
    return {
      modelName: 'OilVolumeChange',
      startDateMenu: false,
      endDateMenu: false,
      serial: null,
      chart: null,
      dataSet: {},
      datePoints: {},
      loading: false,
      firstRun: true,
      showOuterLegend: false,
    }
  },
  computed: {
    goods() {
      return this.$store.getters['data/getDictionary']('goods')
    },
    unitsMeasurementHint() {
      return `label__${this.unitsMeasurement.volumeMeasure}`
    },
    oils() {
      return this.goods.filter(elem => elem.group === 1)
    },
    isMobileDevice() {
      return /Android|webOS|iPhone|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      )
    },
  },
  watch: {
    '$i18n.locale': function(newVal, oldVal) {
      if (this.chart) {
        let yAxes = this.chart.options.scales.yAxes
        if (yAxes && yAxes.length > 0) {
          yAxes[0].scaleLabel.labelString = this.$t(
            `label__${this.unitsMeasurement.volumeMeasure}`
          )
          this.chart.update()
        }
      }
    },
  },
  methods: {
    buildChart() {
      let ctx = document.getElementById('myChart').getContext('2d')
      Chart.defaults.global.elements.point.radius = 0
      return new Chart(ctx, {
        type: 'line',
        data: { labels: [], datasets: [] },
        options: {
          responsive: true,
          maintainAspectRatio: true,
          lineTension: 0,
          legend: {
            display: !this.isMobileDevice,
          },
          scales: {
            yAxes: [
              {
                autoSkip: true,
                scaleLabel: {
                  display: !this.isMobileDevice,
                  labelString: this.$t(
                    `label__${this.unitsMeasurement.volumeMeasure}`
                  ),
                },
              },
            ],
            xAxes: [
              {
                autoSkip: true,
              },
            ],
          },
        },
      })
    },
    setParams(value) {
      this.otherParams = { ...value }
      this.init()
    },
    async init() {
      if (this.extParams) {
        this.otherParams = this.extParams
      }
      let filters = this.getFilters()
      if (!this.firstRun || this.filtersEmpty(filters)) {
        this.getDataSet()
        this.firstRun = false
      }
    },
    getDataSet() {
      this.loading = true
      this.prepareFilterParams()
      this.getOilVolumeChange()
    },
    getOilVolumeChange() {
      this.getData().then(() => {
        if (!this.chart) {
          this.chart = this.buildChart()
        }
        this.updateChart(this.datePoints, this.dataSet)
        this.loading = false
      })
    },
    formatDate(date, granularity) {
      let pattern
      if (granularity === 'day') {
        pattern = 'YYYY-MM-DD'
      } else if (granularity === 'hour') {
        pattern = 'YYYY-MM-DD H:00'
      } else if (granularity === 'minute') {
        pattern = 'YYYY-MM-DD H:mm'
      }
      return moment(date).format(pattern)
    },
    async getData() {
      this.otherParams.start_date = moment(this.params.start_date).valueOf()
      this.otherParams.end_date = moment(this.params.end_date).valueOf()

      await api
        .getReportData(
          'oil_volume_change',
          !this.overrideParams
            ? this.params
            : { ...this.params, ...this.overrideParams }
        )
        .then(response => {
          this.dataSet = response.oilsData
          this.datePoints = response.labels.map(label =>
            this.formatDate(label, this.otherParams.granularity)
          )
        })
    },
    updateChart(labels, data) {
      let colors = {}
      this.oils
        .map(oil => oil.id)
        .sort((a, b) => a - b)
        .forEach((id, index) => {
          colors[id + ''] = index
        })
      let chartDataSets = Object.keys(data).map(oilType => {
        let oilName = this.oils.find(oil => oil.id === parseInt(oilType))
          .shortName
        let color = getColor(oilType)
        return {
          type: 'line',
          data: data[oilType].map(elem =>
            (elem / this.unitsMeasurement.volumeMultiplier).toFixed(2)
          ),
          fill: false,
          borderColor: color,
          backgroundColor: color,
          label: oilName,
          lineTension: 0,
        }
      })
      this.chart.data.labels = labels
      this.chart.data.datasets = chartDataSets
      this.chart.update()
    },
    hashcode(argument) {
      let hash = 0
      for (let i = 0; i < argument.length; i++) {
        let character = argument.charCodeAt(i)
        hash = (hash << 5) - hash + character
        hash = hash & hash
      }
      return hash
    },

    toggleOuterLegend() {
      this.showOuterLegend = !this.showOuterLegend
    },
    toggleLabel(index) {
      var dataItem = this.chart.data.datasets[index]
      if (dataItem.hidden === true || dataItem.hidden === null) {
        dataItem.hidden = false
      } else {
        dataItem.hidden = true
      }
      this.chart.update()
      this.$forceUpdate()
    },
    labelStyle(index) {
      var dataItem = this.chart.data.datasets[index]
      return dataItem && (dataItem.hidden === true || dataItem.hidden === null)
        ? 'text-decoration: line-through'
        : ''
    },
  },
}
</script>

<template>
  <div>
    <parameters
      v-show="!extParams"
      @set-params="setParams($event)"
    />
    <v-progress-linear
      v-if="loading"
      slot="progress"
      height="2.2"
      color="blue"
      background-color="#E0E0E0"
      indeterminate
    />
    <div>
      <canvas
        id="myChart"/>
    </div>
    <div
      v-if="isMobileDevice"
      style="display: grid">
      <v-btn
        @click="toggleOuterLegend()">
        {{ $t('label__legend') }} <v-icon v-if="!showOuterLegend">expand_more</v-icon><v-icon v-else>expand_less</v-icon>
      </v-btn>
    </div>
    <div
      v-if="showOuterLegend && chart !== null && chart.data !== null"
      :class="$style.legendContainer">
      <div
        v-for="(item, index) in chart.data.datasets"
        :key="'chart_legend_'+index"
        :class="$style.legend">
        <div
          :class="$style.rectangle"
          :style="{'background-color': item.backgroundColor }"
          @click="toggleLabel(index)"
        />
        <span
          :style="labelStyle(index)"
        >{{ item.label }}</span>
      </div>
    </div>
  </div>
</template>

<style lang="scss" module>
@import '@design';

.sdTable {
  border: 1px solid #dfdfdf;

  th {
    outline: none;
  }
}

.rectangle {
  width: 40px;
  height: 15px;
  margin-right: 6px;
  margin-left: 12px;
}

.legendContainer {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
}

.legend {
  display: contents;
  font-size: 12px;
}
</style>
